import { useState, useEffect, useCallback, useRef } from 'react'
import { clientesApi } from '../../api/notasVenta'
import useDebounce from '../../hooks/useDebounce'
import { colors, font, text } from '../../design/tokens'
import { Card, Buscador, Cargando, ErrorMsg, Vacio, BotonCargarMas, fmtMoney, Titulo } from './comunes'

// Deudores (cuentas por cobrar) en el celular. Misma fuente que /clientes/deudores:
// feed DEUDORES del FTP de Presea, deuda NETA por cliente (los pagos a cuenta
// restan; solo aparecen los de neto > 0).
export default function MovilDeudores() {
  const [q, setQ] = useState('')
  const qBuscar = useDebounce(q, 350)
  const [filas, setFilas] = useState([])
  const [meta, setMeta] = useState({ total_general: 0, cantidad_clientes: 0, page: 0, last_page: 1 })
  const [detalle, setDetalle] = useState(null) // { cod_cli, nombre, filas } — expandido
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const reqId = useRef(0)

  const cargar = useCallback(async (page) => {
    const id = ++reqId.current
    if (page === 1) setLoading(true)
    try {
      const params = { page, per_page: 30 }
      if (qBuscar.trim()) params.q = qBuscar.trim()
      const { data } = await clientesApi.deudores(params)
      if (id !== reqId.current) return
      setFilas((prev) => (page === 1 ? data.data : [...prev, ...data.data]))
      setMeta({ total_general: data.total_general, cantidad_clientes: data.cantidad_clientes, page: data.page, last_page: data.last_page })
      setError(null)
    } catch {
      if (id === reqId.current) setError('No se pudieron cargar los deudores.')
    } finally {
      if (id === reqId.current) setLoading(false)
    }
  }, [qBuscar])

  useEffect(() => { cargar(1) }, [cargar])

  const verDetalle = async (f) => {
    if (detalle?.cod_cli === f.cod_cli) { setDetalle(null); return }
    setDetalle({ cod_cli: f.cod_cli, nombre: f.nombre, filas: null })
    try {
      const { data } = await clientesApi.deudoresDetalle(f.cod_cli, {})
      setDetalle((prev) => (prev?.cod_cli === f.cod_cli ? { ...prev, filas: data.filas || data.data || [] } : prev))
    } catch {
      setDetalle((prev) => (prev?.cod_cli === f.cod_cli ? { ...prev, filas: [] } : prev))
    }
  }

  return (
    <div>
      <Titulo>Deudores</Titulo>
      <Buscador value={q} onChange={setQ} placeholder="Buscar por nombre o código…" />

      {loading ? <Cargando /> : error ? <ErrorMsg>{error}</ErrorMsg> : (
        <>
          <div style={resumen}>
            <span style={{ fontSize: text.sec, color: colors.textSec }}>
              {Number(meta.cantidad_clientes || 0).toLocaleString('es-AR')} clientes con deuda
            </span>
            <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: 15, color: colors.text }}>
              {fmtMoney(meta.total_general)}
            </span>
          </div>

          {filas.length === 0 ? <Vacio>No hay deudores para esa búsqueda.</Vacio> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filas.map((f) => (
                <Card key={f.cod_cli} onClick={() => verDetalle(f)}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontFamily: font.sans, fontWeight: 600, fontSize: 14, color: colors.text, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.nombre}
                    </span>
                    <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: 13.5, color: colors.err, whiteSpace: 'nowrap' }}>
                      {fmtMoney(f.total)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 3, fontSize: text.sec, color: colors.textMeta, fontFamily: font.sans }}>
                    <span style={monoInline}>Nº {f.cod_cli}</span>
                    <span>· <span style={monoInline}>{f.comprobantes}</span> comprobante{f.comprobantes === 1 ? '' : 's'}</span>
                    {f.desde && <span>· desde <span style={monoInline}>{f.desde}</span></span>}
                    <span style={{ flex: 1 }} />
                    <span>{detalle?.cod_cli === f.cod_cli ? '▲' : '▼'}</span>
                  </div>

                  {detalle?.cod_cli === f.cod_cli && (
                    <div style={{ marginTop: 8, borderTop: `1px solid ${colors.borderSoft}`, paddingTop: 8 }}>
                      {detalle.filas === null ? (
                        <p style={{ margin: 0, fontSize: 12, color: colors.textMeta }}>Cargando detalle…</p>
                      ) : detalle.filas.length === 0 ? (
                        <p style={{ margin: 0, fontSize: 12, color: colors.textMeta }}>Sin detalle disponible.</p>
                      ) : detalle.filas.map((d, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0', fontSize: 12 }}>
                          <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', color: colors.textMeta, flexShrink: 0 }}>{d.fecha}</span>
                          <span style={{ fontFamily: font.sans, color: colors.textSec, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.concepto}{d.nro_comprobante ? ` ${d.nro_comprobante}` : ''}
                          </span>
                          <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: d.es_credito ? colors.ok : colors.text, flexShrink: 0 }}>
                            {fmtMoney(d.monto)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
              {meta.page < meta.last_page && (
                <BotonCargarMas onClick={() => cargar(meta.page + 1)} cargando={false} />
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

const monoInline = { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums' }
const resumen = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
  padding: '10px 2px', marginTop: 4,
}
